/**
 * 퀴즈 폴더 이벤트 카탈로그.
 *
 * 저장 위치 흐름의 이벤트는 모두 `place`(어디서 시작했나)와 `intent`(복사인가 이동인가)를 싣는다 —
 * 저장 완료율을 진입 출처별로 나눠 보기 위해서다.
 */

import type { FolderPlayAction } from '../types/folder.types';

/** 저장 위치 모달 없이 바로 복사하는 자리(플레이·공유 모달). 런타임 허용 목록(`parseQuizCopyPlace`)과 한 곳. */
export const FOLDER_COPY_PLACES = [
  'play_gnb',
  'play_jelly',
  'play_trial_gate',
  'share_modal',
  'copy_other',
] as const;
export type FolderCopyPlace = (typeof FOLDER_COPY_PLACES)[number];

/** 저장·이동 흐름을 시작한 자리. */
export type FolderSavePlace =
  | FolderCopyPlace
  | 'quiz_detail_cta'
  | 'quiz_list_menu'
  | 'folder_detail_card'
  | 'folder_edit_row'
  | 'build_setting_notice';

export type FolderSaveIntent = 'copy' | 'move';

/** 저장 위치 흐름의 공통 문맥. */
export interface FolderSaveContext {
  place: FolderSavePlace;
  intent: FolderSaveIntent;
  quiz_set_id: string;
  /** 내가 만든 퀴즈인가. 소유 여부를 알 수 없는 경로에서는 싣지 않는다. */
  is_quiz_owner?: boolean;
}

export type FolderCreatePlace =
  | 'my_quiz_section'
  | 'my_folder_list'
  | 'my_folder_empty'
  | 'library_empty'
  | 'save_modal';

export type FolderListPlace = 'my_quiz_section' | 'my_folder_list';

export type FolderCardPlace = FolderListPlace | 'library' | 'quiz_row_tag';

export type FolderItemDeletePlace = 'folder_edit_row' | 'folder_detail_menu';

export type FolderPlayPlace = 'folder_detail_cta' | 'folder_detail_row';

export type FolderVisibilityValue = 'PUBLIC' | 'PRIVATE';

/** 서버 응답에 따라 값이 늘 수 있어 문자열로 둔다. */
type FailReason = string;

interface Result {
  result: 'success' | 'fail';
  reason?: FailReason;
}

declare module '@zep/logger' {
  interface EventMap {
    /** 저장·이동·복사 CTA 를 눌렀다. 비로그인이면 로그인으로 보내기 직전에 찍는다. */
    quiz_folder_save_entry_click: FolderSaveContext & {
      auth_required: boolean;
    };
    quiz_folder_save_modal_open: FolderSaveContext & {
      from_folder_id?: string;
      folder_count: number;
      private_only: boolean;
    };
    /** 폴더 목록을 받지 못해 모달을 열지 못했다. */
    quiz_folder_save_modal_fail: FolderSaveContext & { reason: FailReason };
    quiz_folder_save_location_save: FolderSaveContext &
      Result & {
        location_type: 'folder' | 'default';
        target_folder_id?: string;
        target_folder_visibility?: FolderVisibilityValue;
        from_folder_id?: string;
        copied_quiz_set_id?: string;
        publish_confirmed: boolean;
      };
    /** 저장하지 않고 끝났다. `publish_confirm` 은 공개 확인 창에서 취소한 경우다. */
    quiz_folder_save_modal_cancel: FolderSaveContext & {
      cancel_reason: 'dismiss' | 'publish_confirm';
      had_selection: boolean;
    };

    quiz_folder_create_modal_open: { place: FolderCreatePlace };
    quiz_folder_create_modal_save: Result & {
      place: FolderCreatePlace;
      folder_id?: string;
      name_length: number;
    };

    quiz_folder_visibility_change_save: Result & {
      folder_id: string;
      before_visibility: FolderVisibilityValue;
      after_visibility: FolderVisibilityValue;
      confirm_shown: boolean;
      quiz_count: number;
    };
    /** 확인 창에서 확인을 눌렀다. 결과는 뒤따르는 `visibility_change_save` 에 남는다. */
    quiz_folder_visibility_confirm_click: {
      folder_id: string;
      after_visibility: FolderVisibilityValue;
      confirm_stage: 'publish_confirm' | 'hide_confirm';
    };
    quiz_folder_visibility_change_cancel: {
      folder_id: string;
      after_visibility: FolderVisibilityValue;
      cancel_stage: 'publish_confirm' | 'hide_confirm';
    };
    quiz_folder_visibility_upsell_view: {
      folder_id: string;
      trigger: 'client_guard' | 'server_reject';
    };

    quiz_folder_item_delete_save: Result & {
      place: FolderItemDeletePlace;
      folder_id: string;
      quiz_set_id: string;
    };
    quiz_folder_item_delete_cancel: {
      place: FolderItemDeletePlace;
      folder_id: string;
      quiz_set_id: string;
    };
    quiz_folder_list_folder_delete: Result & {
      place: FolderListPlace;
      folder_id: string;
      with_quizzes: boolean;
      quiz_count: number;
    };
    quiz_folder_delete_dialog_cancel: {
      place: FolderListPlace;
      folder_id: string;
      with_quizzes: boolean;
    };

    quiz_folder_detail_page_view: {
      folder_id: string;
      visibility: FolderVisibilityValue;
      quiz_count: number;
      is_folder_owner: boolean;
      play_action: FolderPlayAction;
    };
    quiz_folder_card_click: {
      place: FolderCardPlace;
      folder_id: string;
      position?: number;
    };
    /** 카드가 실제로 화면에 보였을 때 한 번. */
    quiz_folder_card_impression: {
      place: 'library';
      folder_id: string;
      position: number;
    };
    quiz_folder_promo_card_view: { folder_id: string };
    /** 공개 폴더 안의 퀴즈를 숨기려 할 때 뜨는 인라인 안내. */
    quiz_folder_move_notice_view: {
      quiz_set_id: string;
      from_folder_id: string;
    };
    /** `content_type` 은 이미 quiz/board 의미로 쓰여 자료실 전용 이름을 쓴다. */
    quiz_library_content_type_change: {
      before_library_content_type: string;
      after_library_content_type: string;
    };

    quiz_folder_play_entry_click: {
      place: FolderPlayPlace;
      folder_id: string;
      play_action?: FolderPlayAction;
      quiz_count?: number;
      entry_quiz_set_id?: string;
    };
    quiz_folder_play_start_fail: {
      place: FolderPlayPlace;
      folder_id: string;
      reason: FailReason;
    };
    quiz_folder_play_next_fail: {
      folder_id: string;
      quiz_set_id: string;
      reason: FailReason;
    };
  }
}
